import { GuaranteeDto } from 'api/pricing';
import { HtmlSanitized } from 'components/display/HtmlSanitized';
import { ReferentialKey } from 'api/referential';
import { Sizes } from 'components/forms/NumberSelector';
import { useFormikContext } from 'formik';
import { useDialog } from 'hooks/useDialog';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { useMainBorrowerPricingState } from '../../../contexts/main-borrower-pricing/MainBorrowerPricingContext';
import { BorrowerGuaranteeFieldNames, BorrowerGuaranteeFormValues } from '../config/BorrowerGuaranteeFormConfig';
import {
  getGuaranteesToDeactivate,
  getMissingDependencies,
  isGuaranteeAvailable
} from '../config/guaranteeDependenciesConfig';
import { useGuaranteeState } from './useGuaranteeState';

/**
 * Hook principal pour la gestion du formulaire de garanties emprunteur
 * Centralise toute la logique métier
 */
export const useBorrowerGuaranteeForm = () => {
  const { insured, guaranteesReferential } = useMainBorrowerPricingState();
  const { values, setFieldValue } = useFormikContext<BorrowerGuaranteeFormValues>();
  const { openDialog } = useDialog();
  const { activeGuaranteeIds, isITTActive, isIPTActive } = useGuaranteeState();

  // Referentiels
  const civilityReferential = useSelector(selectReferentialDataByKey(ReferentialKey.Civility));
  const loanTypeReferential = useSelector(selectReferentialDataByKey(ReferentialKey.LoanType));

  // Civilité de l'assuré
  const insuredCivility = useMemo(() => {
    if (!insured) return '';
    const civilityRef = civilityReferential.find(ref => ref.id === insured.civilityId);
    return civilityRef?.code || '';
  }, [civilityReferential, insured]);

  // Helper pour obtenir le type de prêt
  const getLoanTypeLabel = useCallback((loanTypeId: string | undefined) => {
    if (!loanTypeId) return '';
    const loanTypeRef = loanTypeReferential.find(item => item.id === loanTypeId);
    return loanTypeRef?.code || '';
  }, [loanTypeReferential]);

  // Garanties obligatoires et optionnelles
  const requiredGuarantees = useMemo(() => {
    return guaranteesReferential.filter(g => g.isRequired);
  }, [guaranteesReferential]);

  const optionalGuarantees = useMemo(() => {
    return guaranteesReferential.filter(g => !g.isRequired);
  }, [guaranteesReferential]);

  // Vérifier si une garantie est active
  const isActive = useCallback((guaranteeId: string) => {
    return activeGuaranteeIds.includes(guaranteeId);
  }, [activeGuaranteeIds]);

  // Vérifier si une garantie est disponible (dépendances satisfaites)
  const isAvailable = useCallback((guarantee: GuaranteeDto) => {
    return isGuaranteeAvailable(guarantee.name, activeGuaranteeIds, guaranteesReferential);
  }, [activeGuaranteeIds, guaranteesReferential]);

  // Obtenir les dépendances manquantes
  const getMissingDeps = useCallback((guarantee: GuaranteeDto) => {
    return getMissingDependencies(guarantee.name, activeGuaranteeIds, guaranteesReferential);
  }, [activeGuaranteeIds, guaranteesReferential]);

  // Toggle une garantie avec gestion des dépendances
  const handleToggleGuarantee = useCallback((guarantee: GuaranteeDto): void => {
    if (guarantee.isRequired) return;

    const currentGuarantees = values[BorrowerGuaranteeFieldNames.Guarantees] || [];
    const guaranteeExists = currentGuarantees.includes(guarantee.id);

    let updatedGuarantees: string[];

    if (guaranteeExists) {
      // Retirer la garantie et toutes ses dépendantes
      const guaranteesToRemove = [
        guarantee.id,
        ...getGuaranteesToDeactivate(guarantee.id, currentGuarantees, guaranteesReferential)
      ];
      updatedGuarantees = currentGuarantees.filter(id => !guaranteesToRemove.includes(id));
    } else {
      // Vérifier les dépendances avant d'ajouter
      if (isGuaranteeAvailable(guarantee.name, currentGuarantees, guaranteesReferential)) {
        updatedGuarantees = [...currentGuarantees, guarantee.id];
      } else {
        console.warn(`Impossible d'activer ${guarantee.name}: dépendances manquantes`);
        return;
      }
    }

    setFieldValue(BorrowerGuaranteeFieldNames.Guarantees, updatedGuarantees);
  }, [values, setFieldValue, guaranteesReferential]);

  // Afficher les détails d'une garantie dans un dialog
  const showGuaranteeDetails = useCallback((title: string, longDescription: string) => {
    openDialog(
      { title, size: Sizes['4xl'] },
      <HtmlSanitized html={longDescription} className="font-12 text-dark-light w-100" />
    );
  }, [openDialog]);

  return {
    // State
    insured,
    insuredCivility,
    isITTActive,
    isIPTActive,
    activeGuaranteeIds,
    requiredGuarantees,
    optionalGuarantees,
    // Helpers
    getLoanTypeLabel,
    isActive,
    isAvailable,
    getMissingDeps,
    // Actions
    handleToggleGuarantee,
    showGuaranteeDetails
  };
};
